<template>
  <div class="character-list">
    <AppNavbar />

    <div class="tag-cloud-section" v-if="popularTags.length">
      <span class="tag-label">Popular Tags:</span>
      <div class="tags-wrapper">
        <button v-for="tag in popularTags" :key="tag.name" class="tag-chip" @click="searchQuery = tag.name; searchCharacters()">
          {{ tag.name }} ({{ tag.count }})
        </button>
      </div>
    </div>

    <div class="filters-bar">
      <div class="filter-group">
        <label>Sort:</label>
        <select v-model="sortBy" @change="sortCharacters">
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="popular">Most Popular (Likes)</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Added By:</label>
        <select v-model="filterAddedBy" @change="searchCharacters">
          <option value="">All Users</option>
          <option v-for="user in uniqueUploaders" :key="user" :value="user">{{ user }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Style:</label>
        <select v-model="filterArtStyle" @change="searchCharacters">
          <option value="">All Styles</option>
          <option v-for="style in uniqueStyles" :key="style" :value="style">{{ style }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Format:</label>
        <select v-model="filterFormat" @change="searchCharacters">
          <option value="">All Formats</option>
          <option v-for="fmt in uniqueFormats" :key="fmt" :value="fmt">{{ fmt.toUpperCase() }}</option>
        </select>
      </div>
      <div class="search-wrapper">
        <input v-model="searchQuery" placeholder="Search..." @input="debouncedSearchCharacters" />
      </div>
    </div>

    <div class="section-container">
      <div v-if="displayList.length" class="card-flex-container">
        <div 
     v-for="character in displayList"
          :key="character.id"
          class="card copy-target"
          :class="{ 'hot-card': character.likes >= 10 }" 
          @click="copyRichImage(character)"
          tabindex="0"
          title="Click to copy"
        >
          <div v-if="character.likes >= 10" class="fire-badge" title="Popular!"><i class="fas fa-fire"></i></div>

          <div class="image-wrapper">
             <div v-if="character.is_ai" class="ai-badge-thumb" title="AI Generated"><i class="fas fa-robot"></i></div>

             <img v-if="getMediaType(character.path) === 'image'" :src="resolveUrl(character.path)" loading="lazy" @error="handleImageError($event)" />
             <video v-else-if="getMediaType(character.path) === 'video'" :src="resolveUrl(character.path)" controls preload="metadata" class="media-player" @click.stop></video>
             <div v-else-if="getMediaType(character.path) === 'audio'" class="audio-container" @click.stop>
                <audio controls :src="resolveUrl(character.path)" class="media-player"></audio>
                <i class="fas fa-music audio-icon-bg"></i>
             </div>
             <iframe v-else :src="getEmbedUrl(character.path)" class="media-player" frameborder="0" allowfullscreen loading="lazy"></iframe>

             <transition name="fade">
                <div class="copy-overlay" v-if="copiedCardId === character.id">
                  {{ getMediaType(character.path) === 'image' ? 'Copied Image!' : 'Copied Code!' }}
                </div>
             </transition>
          </div>

          <div class="card-content">
            <h3>{{ character.name }}</h3>
            <div class="button-group">
              <button class="btn-icon" @click.stop="handleLike(character)" title="Like">
                 <i class="fas fa-thumbs-up" :class="{'liked': hasLiked(character.id)}"></i>
                 <span class="like-count" v-if="character.likes > 0">{{ character.likes }}</span>
              </button>
              
              <button class="btn-icon" @click.stop="openFavoriteModal(character)" title="Save to Group">
                 <i :class="isFavorite(character.id) ? 'fas fa-star fav-active' : 'far fa-star fav-inactive'"></i>
              </button>

              <button class="btn-icon" @click.stop="openCharacterDetails(character)" title="Details">
                 <i class="fas fa-info-circle info-icon"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="no-characters"><p>No items found matching your criteria.</p></div>
    </div>

    <FavoriteGroupModal 
      v-if="activeFavId" 
      :characterId="activeFavId"
      @close="activeFavId = null"
      @change="forceUpdate"
    />
  </div>
</template>

<script>
// import Clipboard from 'clipboard';
import api from '../services/api';
import favService from '../services/favorites'; // NEW IMPORT
import AppNavbar from './AppNavbar.vue';
import FavoriteGroupModal from './FavoriteGroupModal.vue'; // NEW IMPORT

export default {
  components: { AppNavbar, FavoriteGroupModal },
  data() {
    return {
      characters: [], filteredCharacters: [], searchQuery: '', 
      sortBy: 'newest', filterAddedBy: '', filterArtStyle: '', filterFormat: '',
      likedIds: JSON.parse(localStorage.getItem('liked_ids') || '[]'), 
      copiedCardId: null, debounceTimer: null,
      activeFavId: null, // For the modal
      favUpdateTrigger: 0 // To force re-render of star icons
    };
  },
  computed: {
    displayList() {
      let list = [...this.filteredCharacters];
      if (this.filterAddedBy) list = list.filter(c => c.added_by === this.filterAddedBy);
      if (this.filterArtStyle) list = list.filter(c => c.art_style === this.filterArtStyle);
      if (this.filterFormat) {
        list = list.filter(c => {
          const ext = c.path.split('.').pop().toLowerCase();
          if (c.path.includes('youtube') || c.path.includes('vimeo')) return this.filterFormat === 'embed';
          return ext === this.filterFormat;
        });
      }
if (this.sortBy === 'oldest') list.reverse();
      else if (this.sortBy === 'popular') list.sort((a, b) => b.likes - a.likes);
      return list;
    },
    uniqueUploaders() { const users = this.characters.map(c => c.added_by).filter(u => u); return [...new Set(users)].sort(); },
    uniqueStyles() { const styles = this.characters.map(c => c.art_style).filter(s => s); return [...new Set(styles)].sort(); },
    uniqueFormats() {
      const formats = new Set();
      this.characters.forEach(c => {
        if (c.path.includes('youtube') || c.path.includes('vimeo')) formats.add('embed');
        else {
          const ext = c.path.split('.').pop().toLowerCase().split('?')[0];
          if (ext.length < 5) formats.add(ext);
        }
      });
      return Array.from(formats).sort();
    },
    popularTags() {
      const counts = {};
      this.characters.forEach(c => { if (c.tags && Array.isArray(c.tags)) c.tags.forEach(t => { counts[t] = (counts[t] || 0) + 1; }); });
      return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);
    }
  },
  methods: {
    // Media & URL Helpers
    resolveUrl(path) { if (!path) return ''; return path.startsWith('/shared/online/') ? 'https://lms.unb.ca' + path : path; },
    getMediaType(path) {
      if (!path) return 'image'; path = path.toLowerCase().split('?')[0];
      if (path.includes('youtube.com') || path.includes('youtu.be') || path.includes('vimeo.com')) return 'embed';
      if (path.endsWith('.mp4') || path.endsWith('.webm') || path.endsWith('.ogg') || path.endsWith('.mov')) return 'video';
      if (path.endsWith('.mp3') || path.endsWith('.wav')) return 'audio';
      return 'image';
    },
    getEmbedUrl(path) {
      if (!path) return '';
      let url = path;
      if (url.includes('youtube.com/watch?v=')) return url.replace('watch?v=', 'embed/');
      if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'www.youtube.com/embed/');
      return url;
    },
    
    // --- Navigation & Likes ---
    openCharacterDetails(character) { this.$router.push({ name: 'CharacterDetails', params: { id: character.id } }); },
    async handleLike(character) {
      if (this.hasLiked(character.id)) return;
      const id = String(character.id);
      character.likes++;
      this.likedIds.push(id);
      localStorage.setItem('liked_ids', JSON.stringify(this.likedIds));
      try { await api.likeCharacter(character.id); } 
      catch (e) { character.likes--; this.likedIds = this.likedIds.filter(lid => lid !== id); localStorage.setItem('liked_ids', JSON.stringify(this.likedIds)); }
    },
    hasLiked(id) { return this.likedIds.includes(String(id)); },
    
    // --- FAVORITE GROUPS ---
    openFavoriteModal(character) {
      this.activeFavId = character.id;
    },
    isFavorite(id) {
      // depend on trigger to force update
      return this.favUpdateTrigger >= 0 && favService.isFavorite(id);
    },
    forceUpdate() {
      this.favUpdateTrigger++;
    },

    // --- Data & Copy ---
    async fetchCharacters() { try { this.characters = await api.getAllCharacters(); this.searchCharacters(); } catch (error) { console.error(error); } },
    debouncedSearchCharacters() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(this.searchCharacters, 300); },
    searchCharacters() {
       if (!this.searchQuery) this.filteredCharacters = this.characters;
       else {
         const queryTerms = this.searchQuery.toLowerCase().split(',').map(t => t.trim()).filter(t => t);
         this.filteredCharacters = this.characters.filter((character) => {
            const matchesName = queryTerms.some(term => character.name.toLowerCase().includes(term));
            const matchesTags = character.tags && character.tags.some(tag => queryTerms.some(q => tag.toLowerCase().includes(q)));
            return matchesName || matchesTags;
         });
       }
    },
    async copyRichImage(character) {
      if (this.getMediaType(character.path) !== 'image') { navigator.clipboard.writeText(character.htmlCode); this.copiedCardId = character.id; setTimeout(() => { this.copiedCardId = null; }, 2000); return; }
      try {
        const html = character.htmlCode;
        const blobHtml = new Blob([html], { type: "text/html" });
        const blobText = new Blob([html], { type: "text/plain" });
        await navigator.clipboard.write([new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText })]);
        this.copiedCardId = character.id; setTimeout(() => { this.copiedCardId = null; }, 2000);
      } catch (err) { navigator.clipboard.writeText(character.htmlCode); alert("Copied code (Rich paste not supported)"); }
    },
    handleImageError(event) { event.target.src = 'https://via.placeholder.com/300x300?text=Image+Not+Found'; },
  },
  created() { this.fetchCharacters(); },
};
</script>

<style scoped>
/* Reuse existing styles from previous turn */
.card-flex-container { display: flex; flex-wrap: wrap; gap: 20px; padding: 20px; justify-content: flex-start; }
.card { display: flex; flex-direction: column; border: 2px solid #000; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1); background: #fff; position: relative; height: 250px; flex: 0 0 auto; cursor: pointer; transition: transform 0.3s; }
.card:hover { transform: scale(1.03); box-shadow: 0 12px 24px rgba(0,0,0,0.2); }
.image-wrapper { flex: 1; overflow: hidden; position: relative; height: 200px; border-bottom: 3px solid #000; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center; }
.image-wrapper img, .media-player { height: 100%; width: 100%; object-fit: cover; display: block; border: none; }
.audio-container { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #eee; position: relative; }
.audio-container audio { width: 90%; z-index: 2; }
.audio-icon-bg { position: absolute; font-size: 60px; color: rgba(0,0,0,0.1); z-index: 1; }
.card-content { padding: 8px 10px; display: flex; justify-content: space-between; align-items: center; height: 50px; }
.card-content h3 { margin: 0; font-size: 14px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-right: 5px; }
.button-group { display: flex; gap: 4px; align-items: center; flex-shrink: 0; }
.btn-icon { background: none; border: none; cursor: pointer; font-size: 15px; padding: 2px; display: flex; align-items: center; gap: 3px; color: #666; }
.fav-active { color: #ffa933; }
.fav-inactive { color: #ccc; }
.btn-icon:hover .fav-inactive { color: #ffa933; }
.info-icon { color: #2980b9; }
.liked { color: var(--accent-color); }
.like-count { font-weight: bold; font-size: 12px; }
.copy-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(39, 174, 96, 0.9); color: white; padding: 10px 20px; border-radius: 8px; opacity: 1; pointer-events: none; font-weight: bold; white-space: nowrap; }
.no-characters { text-align: center; margin-top: 50px; font-size: 18px; color: #555; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.4s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.section-container { margin-bottom: 40px; }
.tag-cloud-section { padding: 10px 20px; background: #fff; border-bottom: 1px solid #eee; display: flex; align-items: center; gap: 10px; overflow-x: auto; }
.tag-label { font-weight: bold; font-size: 14px; white-space: nowrap; }
.tag-chip { background: #e0f7fa; border: 1px solid #b2ebf2; padding: 4px 10px; border-radius: 12px; cursor: pointer; font-size: 12px; color: #006064; white-space: nowrap; }
.tag-chip:hover { background: #b2ebf2; }
.filters-bar { display: flex; gap: 20px; padding: 15px 20px; background: #f9f9f9; align-items: center; flex-wrap: wrap; }
.filter-group { display: flex; align-items: center; gap: 8px; }
.filter-group select { padding: 6px; border: 1px solid #ccc; border-radius: 4px; }
.search-wrapper input { padding: 8px; border: 1px solid #ccc; width: 200px; border-radius: 4px; }
.hot-card { border-color: #ff5722 !important; box-shadow: 0 0 10px rgba(255, 87, 34, 0.2); }
.fire-badge { position: absolute; top: 10px; right: 10px; color: #ff5722; font-size: 20px; z-index: 5; background: rgba(255,255,255,0.8); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; }
.ai-badge-thumb { position: absolute; top: 10px; left: 10px; color: #5e35b1; font-size: 16px; z-index: 5; background: rgba(255,255,255,0.8); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; }

@media (max-width: 800px) {
  .card { height: 200px; }
  .image-wrapper { height: 150px; }
  .card-content { height: 40px; }
}
</style>